import { NAV_MANIFEST, TRAINING_MODE_NAV } from "@/lib/navigation/nav-manifest";
import { OFF_MENU_SEARCH_PAGES } from "@/lib/navigation/page-directory";

/**
 * Membership test for the static pages the navigation declares, derived from
 * the same manifest the header, the ⌘K index, and the assistants' destination
 * lists are built from — so there is no second list of routes to fall out of
 * date.
 *
 * Deliberately context-free (no role, module, or plugin gating), which is what
 * makes it safe to import from client components: it answers "is this one of
 * the app's real pages", not "may this user open it". Server code that must
 * only name pages a user can actually reach uses `listNavigationDestinations`
 * instead.
 */

function normalizePathname(href) {
  const pathname = String(href || "").split(/[?#]/, 1)[0];
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

function collectHrefs(items, hrefs) {
  for (const item of items || []) {
    const href = typeof item?.href === "string" ? item.href : "";
    // Groups have no href; "#" is a placeholder; external links leave the app;
    // "{workspaceId}" hrefs are per-studio templates with no static pathname.
    if (href && href !== "#" && !item.external && !href.includes("{")) {
      hrefs.add(normalizePathname(href));
    }
    if (Array.isArray(item?.subItems)) collectHrefs(item.subItems, hrefs);
  }
  return hrefs;
}

let manifestPathnames = null;

function getManifestPathnames() {
  if (!manifestPathnames) {
    manifestPathnames = new Set();
    for (const menu of NAV_MANIFEST) collectHrefs(menu.items, manifestPathnames);
    collectHrefs(TRAINING_MODE_NAV.items, manifestPathnames);
    collectHrefs(OFF_MENU_SEARCH_PAGES, manifestPathnames);
  }
  return manifestPathnames;
}

/** True when `href`'s pathname (query/hash ignored) is a manifest-declared page. */
export function isManifestPagePath(href) {
  const pathname = normalizePathname(href);
  return pathname.startsWith("/") && getManifestPathnames().has(pathname);
}
